// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — MarkFoil.js   ·   THE THREE SHELVES, as pixels
// ────────────────────────────────────────────────────────────────────
// One component. Three looks. The look is chosen by the SHELF, never by
// the item — which is the entire point of IE_CANON_PRIME §2.15.
//
//   FABLE      flat foil, ≤3 colours, static.
//   LEGENDARY  the full still build — spectrum, geometry, turbulence,
//              print grain, bloom, milled rim. NOTHING MOVES.
//   MYTHICAL   the SAME build, resolved out of its Legendary through
//              `resolveRecipe`, with motion switched on and the flare
//              that only motion allows layered on top.
//
// ── WHY THERE IS NO `if (mythical)` DESIGN BRANCH ───────────────────
// There is exactly one `animated` boolean and one `flare` object. A
// Mythical cannot look like a different piece from its Legendary,
// because this file is never told what a Mythical looks like — it is
// told what its LEGENDARY looks like and then told to wake it up.
// (ie-3d-conversion: delete the degree of freedom, not the bug.)
//
// ── THE TRAPS THIS FILE ALREADY OBEYS (terror-holofoil) ─────────────
//   • Keyframes go into document.head IMPERATIVELY, never a JSX <style>
//     block — a <style> unmounts with the first instance and kills the
//     animation app-wide (the J'ynxi foil freeze).
//   • STALE-SHEET SELF-HEAL: the guard compares textContent and
//     REPLACES on drift. An id-only guard keeps serving a previous
//     build's sheet after a hot reload and any new keyframe silently
//     never resolves — which is how Solrend's shatter hid for a day.
//   • SVG filter ids are per-instance via useId. A shared global id
//     corrupts every other card on screen.
//   • NO z-index on the foil root. A positioned element with z-index
//     becomes its own stacking context, so the blend modes composite
//     against the wrong thing and the foil turns into a flat wash.
//     Ordering is the HOST's job (isolation:isolate on the slab).
//   • mulberry32, never Math.random(): shard/mote layout is seeded off
//     the recipe seed, so a Mark looks identical on every client and
//     does not reshuffle on re-render.
// ════════════════════════════════════════════════════════════════════

import React, { useId, useMemo, useEffect } from "react";
import { resolveRecipe, variantById, shelfOf } from "./markVariants";
import { MK, mkCut } from "./markData";

// ── seeded PRNG — the law is NO PRNG CALLS DURING RENDER ────────────
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── KEYFRAMES ───────────────────────────────────────────────────────
const FOIL_KEYS_ID = "ie-mark-foil-keys";
const FOIL_KEYS = `
@keyframes mfSpin      { from { transform: rotate(0deg) }   to { transform: rotate(360deg) } }
@keyframes mfSpinRev   { from { transform: rotate(360deg) } to { transform: rotate(0deg) } }
@keyframes mfSheen     { 0% { transform: translateX(-140%) skewX(-18deg) } 100% { transform: translateX(260%) skewX(-18deg) } }
@keyframes mfHue       { from { filter: hue-rotate(0deg) } to { filter: hue-rotate(var(--mfHue, 10deg)) } }
@keyframes mfRim       { 0%,100% { opacity: .45 } 50% { opacity: 1 } }
@keyframes mfLift      { 0%,100% { transform: translate3d(0,0,0) scale(1) } 50% { transform: translate3d(0,-1.5px,0) scale(1.012) } }
@keyframes mfMote      { 0%   { transform: translate(0,0) scale(.6); opacity: 0 }
                         18%  { opacity: .95 }
                         100% { transform: translate(var(--mfx,10px), var(--mfy,-26px)) scale(1.15); opacity: 0 } }
@keyframes mfShard     { 0%,100% { transform: translate(0,0) rotate(0deg); opacity: .55 }
                         42%     { transform: translate(var(--msx,2px), var(--msy,-2px)) rotate(var(--msr,1.2deg)); opacity: 1 } }
@keyframes mfCaustic   { from { background-position: 0% 50% } to { background-position: 220% 50% } }
@keyframes mfRun       { from { transform: translateY(-60%) } to { transform: translateY(160%) } }
`;

function ensureFoilKeys() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(FOIL_KEYS_ID);
  // ⚠ SELF-HEAL. Not `if (el) return`. See the header.
  if (el) {
    if (el.textContent === FOIL_KEYS) return;
    el.remove();
  }
  const st = document.createElement("style");
  st.id = FOIL_KEYS_ID;
  st.textContent = FOIL_KEYS;
  document.head.appendChild(st);
}

// ════════════════════════════════════════════════════════════════════
// FABLE — flat holo. One colour, three at the ceiling. Static.
// It is allowed to be beautiful. It is not allowed to be elaborate.
// ════════════════════════════════════════════════════════════════════
function FableFoil({ colors, opacity = 0.5 }) {
  const c = colors.length ? colors : ["#d4a03c"];
  // One colour → a single soft sweep. Two/three → flat bands, hard-edged
  // enough to read as PRINTED foil rather than as a gradient wash.
  const bg =
    c.length === 1
      ? `linear-gradient(115deg, transparent 12%, ${c[0]}00 24%, ${c[0]}b0 46%, ${c[0]}22 58%, transparent 76%)`
      : `linear-gradient(115deg, ${c
          .map((col, i) => {
            const a = (i / c.length) * 100;
            const b = ((i + 1) / c.length) * 100;
            return `${col}00 ${a}%, ${col}9a ${(a + b) / 2}%, ${col}00 ${b}%`;
          })
          .join(", ")})`;
  return (
    <>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: bg, mixBlendMode: "soft-light", opacity,
      }} />
      {/* the print line — what tells your eye it is foil and not paint */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `repeating-linear-gradient(115deg, ${c[0]}14 0px, ${c[0]}14 1px, transparent 1px, transparent 4px)`,
        mixBlendMode: "overlay", opacity: 0.5,
      }} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE HOLO BUILD — Legendary when `animated` is false, Mythical when
// it is true. ONE function. The shelf never forks the design.
// ════════════════════════════════════════════════════════════════════
function HoloFoil({ r, uid }) {
  const {
    geometry, spectrum = [], spokes = 32, shards = 12, bands = 8,
    grain = "fine", turbulence, bloom, rim, opacity = 0.6,
    animated, flare,
  } = r;

  const F = flare || {};
  const spec = spectrum.length ? spectrum : ["#f6dfa0", "#d4a03c", "#ece6da"];
  const conic = `conic-gradient(from 0deg, ${spec.map((c, i) =>
    `${c} ${Math.round((i / spec.length) * 360)}deg`).join(", ")}, ${spec[0]} 360deg)`;

  const seed = turbulence?.seed ?? 0x50fa;

  // ── seeded geometry, baked once. NEVER called during render. ──────
  const bake = useMemo(() => {
    const R = mulberry32(seed);
    const shardPts = Array.from({ length: shards }, () => {
      const x = R() * 100, y = R() * 100;
      const l = 16 + R() * 46, a = (R() * 180 - 90) * (Math.PI / 180);
      return {
        x1: x, y1: y,
        x2: x + Math.cos(a) * l, y2: y + Math.sin(a) * l,
        w: 0.4 + R() * 1.5,
        dx: (R() * 4 - 2).toFixed(2), dy: (R() * 4 - 2).toFixed(2),
        dr: (R() * 2.4 - 1.2).toFixed(2),
        delay: (R() * 3).toFixed(2),
      };
    });
    const moteN = animated ? (F.motes || 0) : 0;
    const motes = Array.from({ length: moteN }, () => ({
      left: (R() * 100).toFixed(1),
      top: (55 + R() * 45).toFixed(1),
      size: (1 + R() * 2.2).toFixed(2),
      fx: (R() * 30 - 15).toFixed(1),
      fy: (-18 - R() * 30).toFixed(1),
      dur: (3.2 + R() * 3.6).toFixed(2),
      delay: (R() * 5).toFixed(2),
      color: spec[Math.floor(R() * spec.length)],
    }));
    return { shardPts, motes };
  }, [seed, shards, animated, F.motes, spec]);

  const fid = `mf-${uid.replace(/[^a-zA-Z0-9]/g, "")}`;

  // ── LAYER 1 · geometry ────────────────────────────────────────────
  let geo = null;
  if (geometry === "sunburst") {
    geo = (
      <div style={{
        position: "absolute", left: "-45%", top: "-70%", width: "190%", height: "260%",
        pointerEvents: "none",
        background: `repeating-conic-gradient(from 0deg, #ffffff18 0deg, #ffffff18 ${180 / spokes}deg, transparent ${180 / spokes}deg, transparent ${360 / spokes}deg)`,
        mixBlendMode: "overlay", opacity: 0.55,
        animation: animated && F.spin ? `mfSpin ${F.spin}s linear infinite` : "none",
      }} />
    );
  } else if (geometry === "prism") {
    geo = (
      <div style={{
        position: "absolute", inset: "-20%", pointerEvents: "none",
        background: `repeating-linear-gradient(101deg, ${spec.map((c, i) =>
          `${c}00 ${(i * 100) / bands}%, ${c}66 ${((i + 0.5) * 100) / bands}%, ${c}00 ${((i + 1) * 100) / bands}%`).join(", ")})`,
        backgroundSize: animated ? "220% 100%" : "100% 100%",
        mixBlendMode: "color-dodge", opacity: 0.42,
        animation: animated && F.caustic ? `mfCaustic ${F.caustic}s linear infinite` : "none",
      }} />
    );
  } else if (geometry === "fracture") {
    geo = (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        pointerEvents: "none", mixBlendMode: "screen", opacity: 0.7,
      }}>
        <defs>
          <linearGradient id={`${fid}-crack`} x1="0" y1="0" x2="1" y2="1">
            {spec.map((c, i) => (
              <stop key={i} offset={`${(i / (spec.length - 1 || 1)) * 100}%`} stopColor={c} />
            ))}
          </linearGradient>
        </defs>
        {bake.shardPts.map((s, i) => (
          <line
            key={i}
            x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
            stroke={`url(#${fid}-crack)`} strokeWidth={s.w} strokeLinecap="round"
            style={animated && F.shatter ? {
              "--msx": `${s.dx}px`, "--msy": `${s.dy}px`, "--msr": `${s.dr}deg`,
              transformOrigin: "center",
              animation: `mfShard ${F.shatter}s ease-in-out ${s.delay}s infinite`,
            } : { opacity: 0.72 }}
          />
        ))}
      </svg>
    );
  }

  // ── LAYER 2 · the spectrum, displaced by turbulence ───────────────
  // Still on Legendary (fixed seed, no SMIL). Mythical animates the
  // baseFrequency, which is what makes the trapped light RUN.
  const turb = turbulence ? (
    <svg style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none", mixBlendMode: "soft-light", opacity: 0.75,
    }}>
      <defs>
        <filter id={`${fid}-t`} x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency={`${turbulence.freq / 100} ${turbulence.freq / 100}`}
            numOctaves="3" seed={seed % 1000} result="n"
          >
            {animated && (
              <animate
                attributeName="baseFrequency"
                dur={`${(F.sheen || 6) * 3}s`}
                values={`${turbulence.freq / 100} ${turbulence.freq / 100};${(turbulence.freq * 1.9) / 100} ${(turbulence.freq * 0.6) / 100};${turbulence.freq / 100} ${turbulence.freq / 100}`}
                repeatCount="indefinite"
              />
            )}
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="n" scale={turbulence.scale} xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <linearGradient id={`${fid}-s`} x1="0" y1="0" x2="1" y2="1">
          {spec.map((c, i) => (
            <stop key={i} offset={`${(i / (spec.length - 1 || 1)) * 100}%`} stopColor={c} />
          ))}
        </linearGradient>
      </defs>
      <rect x="-10%" y="-10%" width="120%" height="120%" fill={`url(#${fid}-s)`} filter={`url(#${fid}-t)`} />
    </svg>
  ) : null;

  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
      opacity,
      // the whole foil lifts a hair off the slab — mythical only
      animation: animated && F.lift ? `mfLift ${(F.rimPulse || 3) * 2}s ease-in-out infinite` : "none",
    }}>
      {/* base spectrum */}
      <div style={{
        position: "absolute", left: "-30%", top: "-55%", width: "160%", height: "220%",
        background: conic, mixBlendMode: "color-dodge", opacity: 0.3,
        animation: animated && F.spin ? `mfSpinRev ${F.spin * 1.7}s linear infinite` : "none",
      }} />
      {geo}
      {turb}

      {/* print grain — what makes it foil and not a gradient */}
      <div style={{
        position: "absolute", inset: 0,
        background: grain === "glass"
          ? `repeating-linear-gradient(78deg, #ffffff10 0px, #ffffff10 1px, transparent 1px, transparent 7px)`
          : `repeating-linear-gradient(112deg, #ffffff12 0px, #ffffff12 1px, transparent 1px, transparent 3px)`,
        mixBlendMode: "overlay", opacity: 0.6,
      }} />

      {/* bloom */}
      {bloom && (
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(ellipse at 22% 8%, ${bloom}3a, transparent 58%)`,
          mixBlendMode: "screen",
        }} />
      )}

      {/* the sweep. STILL on Legendary — it is parked mid-plate, which is
          a highlight; MOVING on Mythical, which is a sweep. */}
      <div style={{
        position: "absolute", top: "-30%", left: 0, width: "38%", height: "160%",
        background: "linear-gradient(90deg, transparent, #ffffff55, transparent)",
        mixBlendMode: "overlay",
        transform: animated ? undefined : "translateX(58%) skewX(-18deg)",
        animation: animated ? `mfSheen ${F.sheen || 5}s cubic-bezier(.5,0,.5,1) infinite` : "none",
      }} />

      {/* hue drift — the plate walking the spectrum. Mythical only. */}
      {animated && F.hueDrift ? (
        <div style={{
          position: "absolute", inset: 0,
          background: conic, mixBlendMode: "hue", opacity: 0.22,
          "--mfHue": `${F.hueDrift}deg`,
          animation: `mfHue ${(F.sheen || 5) * 2.4}s ease-in-out infinite alternate`,
        }} />
      ) : null}

      {/* motes — mythical only, and the seeded layout never reshuffles */}
      {bake.motes.map((m, i) => (
        <span key={i} style={{
          position: "absolute", left: `${m.left}%`, top: `${m.top}%`,
          width: m.size, height: m.size, borderRadius: "50%",
          background: m.color, boxShadow: `0 0 ${m.size * 4}px ${m.color}`,
          "--mfx": `${m.fx}px`, "--mfy": `${m.fy}px`,
          animation: `mfMote ${m.dur}s linear ${m.delay}s infinite`,
        }} />
      ))}

      {/* the milled rim */}
      {rim && (
        <div style={{
          position: "absolute", inset: 0,
          boxShadow: `inset 0 0 0 1px ${rim}66, inset 0 0 22px ${rim}2e`,
          animation: animated && F.rimPulse ? `mfRim ${F.rimPulse}s ease-in-out infinite` : "none",
          opacity: animated ? undefined : 0.85,
        }} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE ENTRY POINT
// ────────────────────────────────────────────────────────────────────
// Drop inside any isolated slab. NO z-index here, ever — the host
// orders its own children. Renders nothing at all for a variant that
// resolves to no recipe, rather than throwing: a retired Legendary
// must never white-screen somebody's ID card.
// ════════════════════════════════════════════════════════════════════
export default function MarkFoil({ variant, style, className }) {
  const uid = useId();
  useEffect(() => { ensureFoilKeys(); }, []);
  const v = typeof variant === "string" ? variantById(variant) : variant;
  const r = useMemo(() => resolveRecipe(v), [v]);
  if (!r) return null;

  return (
    <div className={className} style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      isolation: "isolate", overflow: "hidden",
      // ⚠ NO OPACITY KNOB. A plate has ONE strength, everywhere it is
      // shown. See THE NO-CATFISH LAW above.
      ...style,
    }}>
      {r.kind === "fable"
        ? <FableFoil colors={r.colors} />
        : <HoloFoil r={r} uid={uid} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ MARKPLATE — every plated surface in the Empire, one definition
// ────────────────────────────────────────────────────────────────────
// The slab, the foil, THE SCRIM and the content, stacked correctly, in
// one place. Every surface that shows a player wears this: the Mark
// card, the Mark chip, the friends panel's header, the shop tile.
//
// caP'n, 2026-08-25: "it only works when I click on the icon — the
// initial mark still has this old look, it should update this as well."
// He is right, and the reason it was ever possible is that three
// surfaces each built their own slab out of the same two tokens. Now
// there is one, so a surface CANNOT be left behind: it either wears
// MarkPlate and is correct, or it does not exist.
//
// ── ⚠ THE SCRIM, AND WHY IT IS NOT OPTIONAL ────────────────────────
// caP'n, same message: "make sure the text is always legible before
// shipping."
//
// A holographic foil is a LIGHTENING effect — color-dodge, screen,
// soft-light — so over a dark slab it does not tint the card, it RAISES
// it, and every piece of low-contrast chrome on top of it disappears.
// That is not a bug in one plate; it is what foils do, and it will
// happen again with every plate anyone ever adds.
//
// So legibility is not a per-plate judgement call. The scrim is a
// FLOOR: an opaque-enough wash of the plate's own deep tone between the
// foil and the text, whose alpha is chosen so that the composite is
// legible even in the worst case — a foil pixel at full white. Then no
// plate, present or future, can take the text with it.
//
// `tools/mark-contrast.js` computes that worst case for every token on
// every plate and is the gate. Run it after touching these numbers.
// ════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════
// ⭐⭐ THE NO-CATFISH LAW ⭐⭐  (caP'n, 2026-08-25)
// ────────────────────────────────────────────────────────────────────
//   "We need absolute consistency and coherency between all shops and
//    systems so users never feel catfished when buying anything from
//    any game."
//
// ### AN ITEM'S APPEARANCE BELONGS TO THE ITEM, NEVER TO THE SURFACE
// ### IT IS SHOWN ON. WHAT YOU SEE IN THE SHOP IS EXACTLY WHAT YOU WEAR.
//
// This was broken, and it was broken by ME: the shop tile ran the foil
// at full strength while the worn card ran it behind a heavy scrim, so
// the same plate was gorgeous on the shelf and dull on the player. That
// is the definition of the thing he is naming — you buy the bright one
// and you get the dark one.
//
// ── HOW IT IS MADE UNREPRESENTABLE, not merely audited ──────────────
// There is now exactly ONE surface in the Empire on which a plate is
// rendered at full strength: `<PlateFace>`. The shop tile mounts it and
// the Mark card mounts it. They cannot look different, because there is
// no second place for the look to live.
//
//   • A FACE is a PRODUCT SHOT. Full foil, no scrim, DISPLAY TEXT ONLY
//     — and every word on it wears FACE_TEXT, whose shadow is what
//     makes bright foil survivable underneath a name.
//   • BODY COPY NEVER GOES ON A FACE. Labels, values, blurbs and rails
//     live on the slab below it, where there is no foil at all and the
//     Mark's own chrome reads exactly as it always did.
//
// That split is why the card can be as bright as the shop AND legible:
// the last two passes both tried to make one surface do both jobs, and
// each time one job lost. The shop tile always had the right shape —
// bright face on top, description on the slab below. The card now has
// the same shape, because it is now literally the same component.
//
// ⚠ ANY new surface that shows a purchasable item — a codex entry, a
// wardrobe, a trade window, another game's shop — mounts `PlateFace`.
// If you find yourself passing an opacity or a scrim to make an item
// "fit" a screen, stop: you are about to catfish somebody.
// ════════════════════════════════════════════════════════════════════

// The shadow every word on a face wears. It is not decoration — it is
// the entire reason display text survives a full-strength foil, and the
// contrast gate models it explicitly (a hard shadow is worth roughly
// half a stop of backdrop, so the gate scores face text against 50%
// black over the brightest the foil can go).
export const FACE_TEXT = {
  textShadow: "0 2px 16px rgba(0,0,0,.86), 0 0 4px rgba(0,0,0,.7), 0 1px 2px rgba(0,0,0,.9)",
};

// ⭐ THE ONE PRODUCT SHOT. Shop tile and Mark card mount THIS.
export function PlateFace({ variant, children, style, className, cut = 0 }) {
  const v = typeof variant === "string" ? variantById(variant) : variant;
  return (
    <div className={className} style={{
      position: "relative",
      ...plateStyle(v),
      border: "none",
      clipPath: cut ? mkCut(cut) : undefined,
      // ⚠ the foil's blend modes composite against THIS box only
      isolation: "isolate",
      overflow: "hidden",
      ...style,
    }}>
      {/* full strength, no scrim. This is the plate as it is SOLD, and
          therefore the plate as it is WORN. */}
      <MarkFoil variant={v} />
      <div style={{ position: "relative", height: "100%" }}>{children}</div>
    </div>
  );
}

// ⭐ THE READER PANEL — how the WHOLE card can be the plate
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-25: "the entire thing should be the cool new mark we
// bought, not just the top header — and make sure it's legible."
//
// Both, and without reopening the dial. The foil runs at FULL STRENGTH
// across the whole card; the blocks of text are stamped INTO it as
// inset panels, the way a real ID card has a printed data block sitting
// on foil stock. So the plate is present everywhere — edges, face,
// through the panels — and every word still has a floor under it.
//
// ⚠ THIS IS NOT A SCRIM AND MUST NOT BECOME ONE. The distinction is
// exact and it is the whole reason this is legal under THE NO-CATFISH
// LAW: a scrim dims THE ITEM, so the item looks different here than in
// the shop. A panel is CHROME LAID ON the item — the plate underneath
// is at the same strength it is everywhere, and what changed is that
// there is a card part in front of it. The shop tile does exactly the
// same thing with its blurb area; this is that idea, inset.
//
// The alpha is measured, not chosen: it is the value at which every
// token on the slab list clears its threshold against a full-white foil
// pixel, and it leaves about a quarter of a full-strength plate visible
// straight through the panel. `gamemaster/tools/mark-contrast.mjs`.
export const PANEL_ALPHA = "c0";
export function ReaderPanel({ variant, children, style, className, cut = 12 }) {
  const v = typeof variant === "string" ? variantById(variant) : variant;
  const deep = (v && v.plate && v.plate.deep) || MK.obsidian;
  return (
    <div className={className} style={{
      position: "relative",
      background: `${deep}${PANEL_ALPHA}`,
      clipPath: cut ? mkCut(cut) : undefined,
      ...style,
    }}>{children}</div>
  );
}

// A shelf badge that is legible by construction: dark ink on the
// shelf's own colour, rather than the shelf's colour as text on a foil
// that happens to be the same hue. (Gold-on-gold was unreadable in the
// shop and would have stayed unreadable on the card.)
export function ShelfChip({ shelf, children }) {
  return (
    <span style={{
      display: "inline-block", background: shelf.tint, color: "#06060a",
      fontFamily: MK.mono, fontSize: 8, letterSpacing: ".2em",
      padding: "2px 7px", clipPath: mkCut(4), whiteSpace: "nowrap",
    }}>{children || `${shelf.glyph} ${shelf.label}`}</span>
  );
}

// The plate's colour, bleeding down into the slab beneath its face as a
// TINT — not a foil. Dark, low-alpha, no blend mode, so it can never
// take the body copy with it. It is what stops the card below the face
// reading as a different object from the face above it.
export function plateAura(variant) {
  const v = typeof variant === "string" ? variantById(variant) : variant;
  const r = resolveRecipe(v);
  const cols = r ? (r.kind === "fable" ? r.colors : (r.spectrum || [])) : [];
  const a = cols[0] || MK.gold;
  const b = cols[1] || cols[0] || MK.violet;
  return {
    background:
      `radial-gradient(ellipse 120% 60% at 12% 0%, ${a}22, transparent 62%),` +
      `radial-gradient(ellipse 100% 50% at 88% 4%, ${b}18, transparent 58%)`,
  };
}

// ⭐ SCRIMS AND MarkPlate ARE DELETED, AND THAT IS THE POINT.
//
// They existed so a surface could dim a plate to suit itself. That
// freedom is the catfish: every dial that lets one screen show an item
// differently from another screen is a dial somebody will turn, and the
// player is the one who finds out. So there is no dial any more, and no
// second renderer to hold one.
//
//     ONE PLATE. ONE RENDERER. ONE STRENGTH. <PlateFace>.
//
// Legibility is solved by SEPARATION instead of by dimming — display
// text on the face, body copy on the slab below it — which costs the
// plate nothing and costs the reader nothing. Two passes were spent
// trying to make a single surface do both jobs; each time one job lost.
// (ie-3d-conversion: delete the degree of freedom, not the bug.)
//
// ⚠ If a future screen "needs" a dimmer plate, it does not. It needs
// its text moved off the face.

// The slab a variant is printed on. Exported so the Mark card, the shop
// tile and any future surface all read ONE definition — a second copy
// of this is how two screens stop looking like one product.
export function plateStyle(variant) {
  const v = typeof variant === "string" ? variantById(variant) : variant;
  const p = v?.plate || { slab: "#12101a", deep: "#0b0910", edge: "#241f31" };
  return {
    background: `linear-gradient(158deg, ${p.slab} 0%, ${p.deep} 52%, #06060a 100%)`,
    border: `1px solid ${p.edge}`,
  };
}

// The shelf's own tint, for badges and rails. One place.
export function shelfBadge(variant) {
  const v = typeof variant === "string" ? variantById(variant) : variant;
  const s = shelfOf(v);
  return { label: s.label, glyph: s.glyph, tint: s.tint, tintHi: s.tintHi };
}
